import { useState, useEffect, useCallback } from 'react';
import { INITIAL_AI_AGENTS } from '../data/aiAgents';
import { apiFetch } from '../utils/api';

/** @typedef {'url'|'file'} KnowledgeSourceType */

/**
 * @typedef {Object} KnowledgeSource
 * @property {string} id
 * @property {KnowledgeSourceType} type
 * @property {string} name
 * @property {number} [size]
 * @property {string} [createdAt]
 * @property {string} [url]
 */

/**
 * @typedef {'active'|'inactive'} IntegrationStatus
 * @typedef {Object} Integration
 * @property {string} id
 * @property {string} name
 * @property {IntegrationStatus} status
 */

/**
 * @typedef {Object} AiAgent
 * @property {string} id
 * @property {string} internalName
 * @property {string} publicName
 * @property {string} [teamId]
 * @property {string} [description]
 * @property {string} prompt
 * @property {KnowledgeSource[]} knowledgeBase
 * @property {Integration[]} integrations
 */

function migrateAgent(agent) {
  const a = { ...agent };
  if (Array.isArray(a.knowledgeBase)) {
    a.knowledgeBase = a.knowledgeBase.map((kb, i) => {
      if (kb.id && (kb.name != null || kb.url != null)) return kb;
      return {
        id: kb.id || `kb_legacy_${a.id}_${i}`,
        type: kb.type || 'url',
        name: kb.name ?? kb.value ?? '',
        url: kb.url ?? (kb.type === 'url' ? kb.value : undefined),
        size: kb.size,
        createdAt: kb.createdAt || new Date().toISOString(),
      };
    });
  }
  return a;
}

/**
 * Hook centralizado para estado e operações dos agentes de IA.
 * Dados mockados com persistência em localStorage; arquitetura pronta para trocar por API.
 * @returns {{
 *   agents: AiAgent[],
 *   selectedAgentId: string | null,
 *   setSelectedAgentId: (id: string | null) => void,
 *   selectedAgent: AiAgent | null,
 *   createAgent: (payload: Partial<AiAgent>) => AiAgent,
 *   updateAgent: (id: string, updates: Partial<AiAgent>) => void,
 *   deleteAgent: (id: string) => void,
 *   addKnowledgeSource: (agentId: string, source: Omit<KnowledgeSource, 'id'|'createdAt'>) => void,
 *   removeKnowledgeSource: (agentId: string, sourceId: string) => void,
 *   addIntegration: (agentId: string, name: string) => void,
 *   setIntegrationStatus: (agentId: string, integrationId: string, status: IntegrationStatus) => void,
 *   removeIntegration: (agentId: string, integrationId: string) => void,
 * }}
 */
export function useAiAgents() {
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);

  // Hydrate selectedAgentId quando a lista carrega (primeiro agente se nenhum selecionado)
  useEffect(() => {
    if (agents.length > 0 && !selectedAgentId) {
      setSelectedAgentId(agents[0].id);
    }
    if (agents.length === 0) {
      setSelectedAgentId(null);
    }
  }, [agents.length, selectedAgentId]);

  useEffect(() => {
    let active = true;
    const loadAgents = async () => {
      try {
        const res = await apiFetch('/ai-agents');
        const list = res?.data?.items ?? res?.items ?? (Array.isArray(res?.data) ? res.data : []);
        if (active) setAgents(list.map(migrateAgent));
      } catch (e) {
        if (active) setAgents(INITIAL_AI_AGENTS);
      }
    };
    loadAgents();
    return () => {
      active = false;
    };
  }, []);

  const selectedAgent = agents.find((a) => a.id === selectedAgentId) || null;

  const createAgent = useCallback(async (payload) => {
    const res = await apiFetch('/ai-agents', {
      method: 'POST',
      json: {
        internalName: payload.internalName ?? 'Novo Agente',
        publicName: payload.publicName ?? 'Novo Agente',
        teamId: payload.teamId ?? null,
        description: payload.description ?? '',
        prompt: payload.prompt ?? '',
        knowledgeBase: Array.isArray(payload.knowledgeBase) ? payload.knowledgeBase : [],
        integrations: Array.isArray(payload.integrations) ? payload.integrations : [],
      },
    });
    const newAgent = migrateAgent(res.data);
    setAgents((prev) => [...prev, newAgent]);
    setSelectedAgentId(newAgent.id);
    return newAgent;
  }, []);

  const updateAgent = useCallback(async (id, updates) => {
    const res = await apiFetch(`/ai-agents/${id}`, {
      method: 'PATCH',
      json: updates,
    });
    const updated = migrateAgent(res.data);
    setAgents((prev) => prev.map((a) => (a.id === id ? { ...a, ...updated } : a)));
  }, []);

  const deleteAgent = useCallback(async (id) => {
    await apiFetch(`/ai-agents/${id}`, { method: 'DELETE' });
    setAgents((prev) => prev.filter((a) => a.id !== id));
    setSelectedAgentId((current) => (current === id ? null : current));
  }, []);

  const addKnowledgeSource = useCallback(async (agentId, source) => {
    const payload = { ...source };
    const res = await apiFetch(`/ai-agents/${agentId}/knowledge`, {
      method: 'POST',
      json: payload,
    });
    const agent = migrateAgent(res.agent || res.data);
    setAgents((prev) => prev.map((a) => (a.id === agentId ? { ...a, ...agent } : a)));
  }, []);

  const removeKnowledgeSource = useCallback(async (agentId, sourceId) => {
    const res = await apiFetch(`/ai-agents/${agentId}/knowledge/${sourceId}`, { method: 'DELETE' });
    const agent = migrateAgent(res.data);
    setAgents((prev) => prev.map((a) => (a.id === agentId ? { ...a, ...agent } : a)));
  }, []);

  const addIntegration = useCallback(async (agentId, name) => {
    const res = await apiFetch(`/ai-agents/${agentId}/integrations`, {
      method: 'POST',
      json: { name },
    });
    const agent = migrateAgent(res.agent || res.data);
    setAgents((prev) => prev.map((a) => (a.id === agentId ? { ...a, ...agent } : a)));
  }, []);

  const setIntegrationStatus = useCallback(async (agentId, integrationId, status) => {
    const res = await apiFetch(`/ai-agents/${agentId}/integrations/${integrationId}`, {
      method: 'PATCH',
      json: { status },
    });
    const agent = migrateAgent(res.data);
    setAgents((prev) => prev.map((a) => (a.id === agentId ? { ...a, ...agent } : a)));
  }, []);

  const removeIntegration = useCallback(async (agentId, integrationId) => {
    const res = await apiFetch(`/ai-agents/${agentId}/integrations/${integrationId}`, {
      method: 'DELETE',
    });
    const agent = migrateAgent(res.data);
    setAgents((prev) => prev.map((a) => (a.id === agentId ? { ...a, ...agent } : a)));
  }, []);

  return {
    agents,
    selectedAgentId,
    setSelectedAgentId,
    selectedAgent,
    createAgent,
    updateAgent,
    deleteAgent,
    addKnowledgeSource,
    removeKnowledgeSource,
    addIntegration,
    setIntegrationStatus,
    removeIntegration,
  };
}
